import React from "react";
import { Input, InputNumber, Select, Switch, DatePicker, Table } from "antd";
import dayjs from "dayjs";
import RewardLinkListField from "./reward-link-list-field";
import RewardTierField from "./reward-tier-field";

const CAMP_LABELS = { shopee: "Shopee", lazada: "Lazada", tiktok: "TikTok" };

/**
 * Ma trận tuỳ chỉnh theo sàn — mỗi sàn có thể đè hệ số & phạm vi nhân riêng.
 * Để trống ô nào = sàn đó dùng cấu hình chung. value dạng
 * { shopee: { multiplier?, scope? }, ... }
 */
function CampMatrixField({ field, value, onChange }) {
    const rules = value || {};
    const setRule = (camp, key, val) => {
        const next = { ...rules, [camp]: { ...(rules[camp] || {}) } };
        if (val === null || val === undefined || val === "") delete next[camp][key];
        else next[camp][key] = val;
        if (!Object.keys(next[camp]).length) delete next[camp];
        onChange(field.key, next);
    };

    return (
        <div className="w-full">
            <Table
                size="small"
                pagination={false}
                rowKey="camp"
                dataSource={(field.camps || []).map((camp) => ({ camp }))}
                columns={[
                    { title: "Sàn", dataIndex: "camp", width: 90, render: (c) => CAMP_LABELS[c] || c },
                    {
                        title: "Hệ số riêng", width: 130,
                        render: (_, r) => (
                            <InputNumber
                                min={1} step={0.5} placeholder="chung"
                                value={rules[r.camp]?.multiplier}
                                onChange={(v) => setRule(r.camp, "multiplier", v)}
                                style={{ width: 110 }}
                            />
                        ),
                    },
                    {
                        title: "Phạm vi riêng", width: 170,
                        render: (_, r) => (
                            <Select
                                allowClear placeholder="chung"
                                value={rules[r.camp]?.scope}
                                onChange={(v) => setRule(r.camp, "scope", v ?? null)}
                                options={field.scopeOptions || []}
                                style={{ width: 150 }}
                            />
                        ),
                    },
                ]}
            />
            {field.hint && <p className="text-xs text-content-3 mt-1">{field.hint}</p>}
        </div>
    );
}

/**
 * DynamicConfigField — render 1 field cấu hình riêng theo type chương trình.
 * Spec do BE trả về từ /admin-reward-program/types (handler.configFields):
 *   { key, type: number|text|select|multiselect|switch|date, label, default?, min?, max?, step?, options? }
 * Thêm loại chương trình mới → form tự mọc field, KHÔNG sửa file admin nào.
 */
function DynamicConfigField({ field, value, onChange }) {
    const v = value === undefined ? field.default : value;

    switch (field.type) {
        case "number":
            return (
                <InputNumber
                    min={field.min}
                    max={field.max}
                    step={field.step || 1}
                    value={v}
                    onChange={(val) => onChange(field.key, val)}
                    style={{ width: 180 }}
                />
            );
        case "select":
            return (
                <Select
                    value={v}
                    onChange={(val) => onChange(field.key, val)}
                    options={(field.options || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o))}
                    // Nhãn option do handler viết, có câu dài ("Chưa có đơn nào trước ngày
                    // bắt đầu đợt (khuyến nghị)") — đóng cứng 220px là cắt mất chữ.
                    style={{ width: "100%", minWidth: 280 }}
                />
            );
        case "multiselect":
            return (
                <Select
                    mode="multiple"
                    value={v || []}
                    onChange={(val) => onChange(field.key, val)}
                    options={(field.options || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o))}
                    style={{ width: "100%", minWidth: 280 }}
                />
            );
        case "switch":
            return <Switch checked={!!v} onChange={(val) => onChange(field.key, val)} />;
        case "camp_matrix":
            return <CampMatrixField field={field} value={v} onChange={onChange} />;
        case "tier_table":
            // Bảng mốc thưởng theo giá trị đơn (đợt thưởng đơn đầu user mới).
            return <RewardTierField field={field} value={v} onChange={onChange} />;
        case "link_list":
            // Whitelist shop/sản phẩm: admin dán link, BE tách id (xem reward-link-list-field).
            return <RewardLinkListField field={field} value={v} onChange={onChange} />;
        case "date":
            return (
                <DatePicker
                    showTime
                    value={v ? dayjs.unix(Number(v)) : null}
                    onChange={(val) => onChange(field.key, val ? val.unix() : null)}
                />
            );
        default: // 'text' + type lạ từ handler mới → fallback text, không crash form cũ
            return <Input value={v || ""} onChange={(e) => onChange(field.key, e.target.value)} style={{ maxWidth: 400 }} />;
    }
}

export default DynamicConfigField;
